import {
  Label, ComboBox, Input,
  RecordInterface, SetFormValues,
} from '@components/common/Forms';
import { useState } from 'react';
import { SetMirrorData } from '@type/job-add';
import { SortedSubscriber, sortJobSubscribers } from '@components/templates/job-add/edit/functions/jobSubscribersFunctions';
import { useTranslation } from 'next-i18next';

/** Props */
interface JobSubscribersProps {
  apiJobSubscribers?: ApiSchema<'SubscriberRead'>[],
  record: RecordInterface,
  formValues: object,
  setFormValues: SetFormValues,
  initialSelectedValues?: number[],
  checkboxName?: string,
  labelDescriptionClass?: string,
  setMirrorData?: SetMirrorData,
}


/**
 * @description Job Subscribers component. To be used in a form. The text input field is omitted from submit.
 * - `apiJobSubscribers` - static list of subscribers from the API
 * - `record` - record function from useBjForm
 * - `formValues` - form values object from useBjForm
 * - `setFormValues` - setFormValues function from useBjForm
 * - `initialSelectedValues` - initial selected values for combobox (ie: useBjForm.withDefaultValues.careerLevels)
 * - `checkboxName` - name of the checkbox; default is 'subscribers'
 * - `labelDescriptionClass` - additional classes for the label description
 * - `setMirrorData` - function to update the mirror form state
 */
export const JobSubscribers = (props: JobSubscribersProps) => {
  // Destructure props
  const {
    apiJobSubscribers,
    record,
    formValues,
    setFormValues,
    initialSelectedValues,
    checkboxName = 'subscribers',
    labelDescriptionClass = '',
    setMirrorData,
  } = props;

  // Translation
  const { t } = useTranslation();

  // Sort subscribers by type. Add titles for each type. Add recruiter title and checkbox.
  const [sortedJobSubscribers] = useState(sortJobSubscribers(apiJobSubscribers));

  // Render
  // **************************
  return (
    <Label
      as="div"
      description={t('job.add.label.assign_to')}
      descriptionClass={labelDescriptionClass}
    >
      <ComboBox
        className="max-w-full text-ellipsis pr-18"
        options={{
          staticList: sortedJobSubscribers,
          valueFromKey: 'name',
          translateFn: t,
          unmount: false,
          keepOpen: true,
          behaveAs: 'select',
          isItemDisabled: (item) => item['isTitle' as keyof typeof item] === true,
          selectOptions: {
            multiple: true,
            checkboxName: [checkboxName],
            checkboxValueFromKey: 'id',
            setFormValues,
            initialSelectedValues: [
              ...initialSelectedValues ?? [],
            ],
            updatedSelectedValues: formValues?.[checkboxName as keyof typeof formValues],
          },
        }}
        onBlur={() => setMirrorData?.()}
        onClear={() => {
          setFormValues({ [checkboxName]: [] });
          setMirrorData?.();
        }}
        {...record('subscribersSelector', {
          removeFromSubmit: true,
        })}
        renderList={(
          item,
          index,
          listKey,
          getItemProps,
          listStyling,
        ) => {
          const subscriber = item as SortedSubscriber;
          return (
            <li key={listKey} {...getItemProps({ item, index })} className="px-3">

              {/* Category title */}
              {subscriber?.isTitle && (
                <span className={`${listStyling} relative -mb-0.5 items-center text-pico font-semibold uppercase`}>
                  {t(subscriber?.name || '')}
                </span>
              )}

              {/* Default item */}
              {!subscriber?.isTitle && (
                <span className={`${listStyling} cursor-pointer items-center text-sm`}>
                  <Input
                    type="checkbox"
                    {...record(checkboxName, {
                      id: `${checkboxName}-${subscriber?.id}`,
                      defaultValue: subscriber?.id,
                      convertValue: 'toNumber',
                      valueAsArray: true,
                    })}
                  />
                  <span className="pl-2.5">
                    {subscriber?.name}
                    <span className="font-normal">
                      {` (${subscriber?.email})`}
                    </span>
                  </span>
                </span>
              )}
            </li>
          );
        }}
      />
    </Label>
  );
};
